const _mediaQueryWatcher =
  typeof window.installMediaQueryWatcher === 'function'
    ? window.installMediaQueryWatcher
    : (mediaQuery, changedCallback) => {
        const mq = window.matchMedia(mediaQuery);
        mq.addEventListener('change', (e) => changedCallback(e.matches));
        changedCallback(mq.matches);
      };

class MobileDock extends HTMLElement {
  constructor() {
    super();
    this.isShow = true;
    this._hasScrolled = false;
    this._isInitialized = false;
    this._hasMediaListener = false;
    this._lastHeaderHidden = false;

    this._onEditorSelect = this._onEditorSelect.bind(this);
    this._onEditorDeselect = this._onEditorDeselect.bind(this);
    this._onEditorLoad = this._onEditorLoad.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    if (!this._hasMediaListener) {
      this._hasMediaListener = true;
      _mediaQueryWatcher(`(max-width: ${MIN_DEVICE_WIDTH.tablet_max}px)`, (isMobile) => {
        if (isMobile) {
          if (this.isDesignMode) {
            document.addEventListener("shopify:section:load", this._onEditorLoad);
            document.addEventListener("shopify:section:select", this._onEditorSelect);
            document.addEventListener("shopify:block:select", this._onEditorSelect);
            document.addEventListener("shopify:section:deselect", this._onEditorDeselect);
            document.addEventListener("shopify:block:deselect", this._onEditorDeselect);
          }
          if (!this._isInitialized) this._init();
        } else {
          this._destroy();
        }
      });
    }

    if (this.isDesignMode) {
      document.addEventListener("shopify:section:load", this._onEditorLoad);
      document.addEventListener("shopify:section:select", this._onEditorSelect);
      document.addEventListener("shopify:block:select", this._onEditorSelect);
      document.addEventListener("shopify:section:deselect", this._onEditorDeselect);
      document.addEventListener("shopify:block:deselect", this._onEditorDeselect);
    }
  }

  disconnectedCallback() {
    if (this.isDesignMode) {
      document.removeEventListener("shopify:section:load", this._onEditorLoad);
      document.removeEventListener("shopify:section:select", this._onEditorSelect);
      document.removeEventListener("shopify:block:select", this._onEditorSelect);
      document.removeEventListener("shopify:section:deselect", this._onEditorDeselect);
      document.removeEventListener("shopify:block:deselect", this._onEditorDeselect);
    }
    this._destroy();
  }

  get isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  _init() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    document.body.classList.add('has-mobile-dock', 'mobile-dock-enabled');

    this._syncComparisonFlag = this._syncComparisonFlag.bind(this);
    this._comparisonItemEl = this.querySelector('[data-dock-comparison]');

    this._headerEl = document.querySelector('.section-header');
    if (!this._headerEl) {
      this._setDockState(false);
      return;
    }

    const isHeaderSticky =
      this._headerEl.querySelector('[data-is-sticky]')?.dataset.isSticky === 'true';

    this._setDockState(true);

    this._hasScrolled = window.scrollY > 0;
    this._lastHeaderHidden = isHeaderSticky ? this._isHeaderHidden() : false;
    if (!this._hasScrolled) {
      window.addEventListener('scroll', this._onFirstScroll, { passive: true });
    }

    if (isHeaderSticky) {
      this._initStickyMode();
    } else {
      this._initNonStickyMode();
    }

    this._syncComparisonFlag();
  }

  _destroy() {
    if (!this._isInitialized) return;
    this._isInitialized = false;

    this._headerObserver?.disconnect();
    this._headerIntersectionObserver?.disconnect();
    this._comparisonStyleObserver?.disconnect();

    this._headerObserver = null;
    this._headerIntersectionObserver = null;
    this._comparisonStyleObserver = null;
    this._comparisonItemEl = null;
    this._headerEl = null;

    window.removeEventListener('scroll', this._onFirstScroll);
    if (this._stickyRafId) {
      cancelAnimationFrame(this._stickyRafId);
      this._stickyRafId = null;
    }

    document.body.classList.remove('has-mobile-dock', 'has-mobile-dock-comparison', 'mobile-dock-enabled');
    this.classList.remove('mobile-dock--hidden');

    this.isShow = true;
    this._hasScrolled = false;
    this._lastHeaderHidden = false;
  }

  _initNonStickyMode() {
    this._headerIntersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!this._hasScrolled) return;
        this._setDockState(!entries[entries.length - 1].isIntersecting);
      },
      { threshold: 0 }
    );
    this._headerIntersectionObserver.observe(this._headerEl);
  }

  _initStickyMode() {
    this._headerObserver = new MutationObserver(() => {
      if (!this._hasScrolled || this._stickyRafId) return;
      this._stickyRafId = requestAnimationFrame(() => {
        this._stickyRafId = null;
        this._checkStickyVisibility();
      });
    });
    this._headerObserver.observe(this._headerEl, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  _setDockState(isShow) {
    if (this.isShow === isShow) return;
    this.isShow = isShow;
    this.classList.toggle('mobile-dock--hidden', !isShow);
    document.body.classList.toggle('has-mobile-dock', isShow);
    this._syncComparisonFlag();
  }

  _isHeaderHidden() {
    if (!this._headerEl) return false;
    const classes = this._headerEl.classList;
    return (
      classes.contains('header-up') ||
      classes.contains('header-up-always') ||
      classes.contains('not-scroll-up')
    );
  }

  _checkStickyVisibility(force = false) {
    if (!this._headerEl) return;

    const isHeaderHidden = this._isHeaderHidden();

    if (!force && isHeaderHidden === this._lastHeaderHidden) return;
    this._lastHeaderHidden = isHeaderHidden;

    this._setDockState(isHeaderHidden);
  }

  _syncComparisonFlag = () => {
    const shouldHideWidget =
      this._isInitialized &&
      this.isShow &&
      !!this._comparisonItemEl;

    if (document.body.classList.contains('has-mobile-dock-comparison') !== shouldHideWidget) {
      document.body.classList.toggle('has-mobile-dock-comparison', shouldHideWidget);
    }
  };

  _onFirstScroll = () => {
    this._hasScrolled = true;
    window.removeEventListener('scroll', this._onFirstScroll);
  };

  // Theme editor handlers
  _onEditorLoad(event) {
    if (!this.isDesignMode) return;

    const loadedSectionId = event.detail?.sectionId;
    if (loadedSectionId === this.sectionId) {
      this._destroy();
      this._init();
    }
  }

  _onEditorSelect(event) {
    if (!this.isDesignMode) return;

    const selectedSectionId = event.detail?.sectionId;
    if (selectedSectionId === this.sectionId) {
      this._setDockState(true);
    }
  }

  _onEditorDeselect(event) {
    if (!this.isDesignMode) return;

    const deselectedSectionId = event.detail?.sectionId;
    if (deselectedSectionId === this.sectionId) {
      if (this._headerEl) {
        this._checkStickyVisibility(true);
      }
    }
  }
}

if (!customElements.get('mobile-dock')) {
  customElements.define('mobile-dock', MobileDock);
}